import { useEnum } from "@/api/services/global/enum";
import { TJobListOutputDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { JobTagList } from "@/features/job/components/JobTagList";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { divider } from "@/theme/colors";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type TJobListTableProps = {
  rows: TJobListOutputDto[];
  isLoading: boolean;
};

export const JobListTable: FC<TJobListTableProps> = ({ rows, isLoading }) => {
  const navigate = useNavigate();
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, 15);
  };

  const columns = useMemo<MRT_ColumnDef<TJobListOutputDto>[]>(
    () => [
      {
        id: "status",
        header: "ステータス",
        accessorKey: "status",
        maxSize: 134,
        Cell: ({ row }) => {
          return <JobStatusTag status={row.original?.status} title={findLabelByValue(jobStatusOptions, row.original.status)} variant="bold" />;
        },
        Header: <TableSorting name="job.status">ステータス</TableSorting>,
      },
      {
        id: "tag",
        header: "タグ",
        Cell: ({ row }) => {
          return <JobTagList tag={row.original?.tag ?? []} createdAt={row.original?.createdAt} />
        },
        muiTableHeadCellProps: {
          sx: { maxWidth: 150 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 150 },
        },
      },
      {
        id: "managementNumber",
        header: "求人番号",
        maxSize: 88,
        Cell: ({ row }) => truncateText(row.original?.managementNumber) ?? EMPTY_STR.TEXT,
      },
      {
        id: "companyName",
        header: "企業名",
        maxSize: 200,
        Cell: ({ row }) => truncateText(row.original?.company?.companyName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="company.companyName">企業名</TableSorting>,
      },
      {
        id: "jobName",
        header: "求人タイトル",
        maxSize: 156,
        Cell: ({ row }) => truncateText(row.original?.jobName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="job.jobName">求人タイトル</TableSorting>,
      },
      {
        id: "companySpecificIndustryId",
        header: "分野",
        maxSize: 92,
        Cell: ({ row }) => truncateText(row.original?.companySpecificIndustry?.companySpecificIndustry) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="companySpecificIndustry.companySpecificIndustry">分野</TableSorting>,
      },
      {
        id: "recruitmentNumber",
        header: "募集人数",
        maxSize: 86,
        Cell: ({ row }) => (row.original?.recruitmentNumber != null ? `${row.original.recruitmentNumber}名` : EMPTY_STR.TEXT),
      },
      {
        id: "workplace",
        header: "勤務地",
        maxSize: 131,
        Cell: ({ row }) => truncateText(row.original?.workplace) ?? EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentLocationType",
        header: "募集地",
        maxSize: 84,
        Cell: ({ row }) =>
          row.original.recruitmentLocationType
            ? truncateText(findLabelByValue(recruitmentLocationTypeOptions, row.original.recruitmentLocationType))
            : EMPTY_STR.TEXT,
      },
      {
        id: "createdAt",
        header: "登録日",
        maxSize: 106,
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format("YYYY/MM/DD") : EMPTY_STR.TEXT),
        Header: <TableSorting name="job.createdAt">登録日</TableSorting>,
      },
    ],
    [jobStatusOptions],
  );

  const toDetailRoute = (jobId: number) => {
    const route = getDynamicRoute(AppRoutes.jobDetail, { jobId: jobId });
    navigate(route);
  };

  const [heightTable] = useState<string>("calc(100vh - 494px)");

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toDetailRoute(row.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
  });

  return <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={48} bodyRowHeight={64} borderColor={divider.high} />;
};
